import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Location, Organization } from "../../../../Interfaces"
import Map from "../../../../components/maps/Map"
import { FormEvent, useRef, useState, useEffect } from "react"
import { locationService } from "../../../../services/locationService"
import SaveButtonWithStatus from "../../../../components/general/SaveButtonWithStatus"
import ConfirmationModal from "../../../../components/modals/ConfirmationModal"
import { GiCrosshair } from "react-icons/gi"
import { useQuery } from "@tanstack/react-query"
import { managementCompanyService } from "../../../../services/managementCompanyService"
import { organizationService } from "../../../../services/organizationService"
import useUser from "../../../../hooks/useUser"
import { COMPANY_MANAGER } from "../../../../utils/roles"
import CustomSelect from "../../../../components/forms/CustomSelect"
import DynamicInput from "../../../../components/forms/DynamicInput"
import LocationHeader from "../../../../components/locations/LocationHeader"

export default function UpdateLocation() {
  const { roleId } = useUser()
  const location = useLocation()
  const { id } = useParams()
  const locationId = Number(id)
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null)
  const [zoom, setZoom] = useState<number>(18)
  const [savingStatus, setSavingStatus] = useState<null | string>(null)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0)
  const [locationName, setLocationName] = useState<string>("")
  const navigate = useNavigate()
  const coordinates = useRef<google.maps.LatLngLiteral>({ lat: 0, lng: 0 })

  // Fetch location data if not in state
  useEffect(() => {
    const fetchLocation = async () => {
      if (!locationId) return
      try {
        const locationData = await locationService.getOne(locationId)
        if (locationData) {
          setCurrentLocation(locationData)
          setZoom(locationData.zoomLevel || 18)
          setSelectedOrganizationId(locationData.organizationId || 0)
          setLocationName(locationData.name)
          coordinates.current = {
            lat: locationData.latitude,
            lng: locationData.longitude
          }
        }
      } catch (error) {
        console.error("Error fetching location:", error)
      }
    }

    // If we have location state, use it; otherwise fetch the location
    if (location.state?.currentLocation) {
      const locationData = location.state.currentLocation
      setCurrentLocation(locationData)
      setZoom(locationData.zoomLevel || 18)
      setSelectedOrganizationId(locationData.organizationId || 0)
      setLocationName(locationData.name)
      coordinates.current = {
        lat: locationData.latitude,
        lng: locationData.longitude
      }
    } else {
      fetchLocation()
    }
  }, [locationId, location.state])

  const { data: ManagementCompany } = useQuery(["management-company", locationId], () => managementCompanyService.getByLocation(locationId), {
    enabled: locationId !== undefined
  })

  const { data: organizationData } = useQuery(["organizations"], () => organizationService.getAllByManagementCompany(ManagementCompany?.id || 0), {
    enabled: ManagementCompany !== undefined && ManagementCompany.id !== 0,
  })

  const { data: organizationName } = useQuery<string>(["organization-name", locationId], () => locationService.getOrganizationName(locationId), {
    enabled: locationId !== undefined,
  })

  const { data: managementCompanyName } = useQuery<string>(["management-company-name", locationId], () => locationService.getManagementCompanyName(locationId), {
    enabled: locationId !== undefined,
  })

  const handleUpdate = async () => {
    if (!currentLocation) return
    setSaveStatus("Saving...")
    try {
      const status = await locationService.update({ ...currentLocation, managementCompanyId: ManagementCompany?.id, organizationId: selectedOrganizationId, name: locationName })
      if (status) {
        setSaveStatus("Saved!")
        setTimeout(() => {
          setSaveStatus(null)
        }, 1500)
      } else {
        setSaveStatus("Error")
        setTimeout(() => {
          setSaveStatus(null)
        }, 1500)
      }
    } catch (error) {
      setSaveStatus("Error")
      setTimeout(() => {
        setSaveStatus(null)
      }, 1500)
    }
  }

  const handleSaveZoomAndCenter = async () => {
    if (!currentLocation) return
    setSavingStatus("Saving...")
    try {
      const status = await locationService.update({ ...currentLocation, zoomLevel: zoom })
      if (status) {
        setSavingStatus("Saved!")
        setTimeout(() => {
          setSavingStatus(null)
        }, 1500)
      } else {
        setSavingStatus("Error")
        setTimeout(() => {
          setSavingStatus(null)
        }, 1500)
      }
    } catch (error) {
      setSavingStatus("Error")
      setTimeout(() => {
        setSavingStatus(null)
      }, 1500)
    }
  }

  const handleLocationAdjustment = (latLng: google.maps.LatLng | google.maps.LatLngLiteral) => {
    if (!currentLocation) return
    coordinates.current.lat = Number(latLng.lat)
    coordinates.current.lng = Number(latLng.lng)
    setCurrentLocation({
      ...currentLocation,
      latitude: Number(latLng.lat),
      longitude: Number(latLng.lng)
    })
  }

  const handleDeleteLocation = async () => {
    if (!currentLocation?.id) return
    try {
      const status = await locationService.delete(currentLocation.id)
      if (status) {
        setShowDeleteConfirmationModal(false)
        navigate("/app/locations")
      }
    } catch (error) {
      console.error("Error deleting location:", error)
      // You could show an error message to the user here if needed
    }
  }

  const handleOrganizationSelect = (organization: Organization) => {
    setSelectedOrganizationId(organization.id)
  }

  const handlLocationNameChange = (e: FormEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) return
    setLocationName(e.currentTarget.value)
  }

  if (!currentLocation) return null

  return (
    <div className="w-full min-h-full flex justify-center">
      <div className="w-full flex flex-col">
        <LocationHeader
          location={currentLocation}
          activeTab="Update Location"
          organizationName={organizationName}
          managementCompanyName={managementCompanyName}
        />

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-lg p-4">
          <div className="flex flex-col gap-4">
            {/* Location Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Location Details</h2>
                <div className="flex gap-2">
                  <SaveButtonWithStatus saveStatus={saveStatus} handleSave={handleUpdate} />
                  <button
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    onClick={() => setShowDeleteConfirmationModal(true)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                  <DynamicInput
                    value={locationName}
                    name="locationName"
                    type="text"
                    onChange={handlLocationNameChange}
                  />
                </div>
                {roleId && roleId >= COMPANY_MANAGER && organizationData && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                    <CustomSelect
                      list={organizationData}
                      placeholder="Select areas(s)"
                      className="rounded-lg"
                      styles={{ background: "#daf1ff" }}
                      defaultValues={currentLocation.organizationId}
                      handleSelection={handleOrganizationSelect}
                      required={true}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Map Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-semibold mb-4">Location Map</h2>
              <div className="relative h-[calc(100vh-40.625rem)]">
                <GiCrosshair className="text-secondary text-5xl absolute z-10 top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2" />
                <Map
                  zoom={zoom}
                  setZoom={setZoom}
                  name={currentLocation.name}
                  coordinates={coordinates.current}
                  handleLocationAdjustment={handleLocationAdjustment}
                />
                <SaveButtonWithStatus
                  saveStatus={savingStatus}
                  handleSave={handleSaveZoomAndCenter}
                  text="Save Zoom & Center"
                  className="absolute w-50 bottom-1 left-1 shadow-right-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirmationModal && (
        <ConfirmationModal
          isOpen={showDeleteConfirmationModal}
          onConfirm={handleDeleteLocation}
          onCancel={() => setShowDeleteConfirmationModal(false)}
          title="Delete Location"
          message="Are you sure you want to delete this location?"
        />
      )}
    </div>
  )
}
